import type { ProcessingNotification } from "@repo/helpers";

import { updateValuesWrapper } from "@repo/dynamicEditor/src/helpers/updatePropertiesHooks";
import { hasValue, PushProcessingNotification } from "@repo/helpers";
import { BasicDialog, useAlertContext } from "@repo/ui";
import { addBusinessDays, addDays } from "date-fns";
import { useCallback, useMemo, useState } from "react";

import type { ITemplateData } from "../../../components/dynamicEditor/templateDataHook";
import type { Complaint } from "../../../models";

import { useComplaintApi, useComplaintQueries } from "../../../api/complaintApi";
import { getItems } from "../../../api/queries";
import { useTabCountQuery } from "../../../api/repApi";
import { useRepIncidentQueries } from "../../../api/repIncidentApi";
import { ConfirmAlertDialog } from "../../../components/confirmAlertDialog";
import { EditableItemDynamicTab } from "../../../components/dynamicEditor/dynamicTab/editableItem";
import { useComplaintContext, useRepContext } from "../../../contexts/editableItemContext";
import { useEntityId } from "../../../helpers/urlHooks";
import { tabs } from "../../../string";
import { useIncidentLists } from "../../repIncident/incidentLists";
import { PotentialDuplicatesGrid } from "./potentialDuplicatesGrid";
import { RepDisciplineEditorModal } from "./repDisciplineEditor";

interface IState extends ITemplateData {
	incident: Complaint;
}

export default function ComplaintEditorDynamic() {
	const context = useComplaintContext();
	const { checkForDuplicates } = useDuplicateCheck();
	const { repId, rep, editItem: editRep } = useRepContext();
	const { item: incident, editItem: editComplaint, overwriteItem: overwriteComplaint } = context;
	const { activeLists: incidentLists, allLoaded: listsLoaded } = useComplaintLists();
	const { saveComplaint, saveComplaintDocs, loadComplaint, reassignAgent, assignToAnalyst, assignToRepresentative } =
		useComplaintApi();
	const {
		findAgent: findAgentApi,
		findEnrollment: findEnrollmentApi,
		attachEnrollment: attachEnrollmentToComplaint,
	} = useComplaintApi();
	const { useComplaintQuery } = useComplaintQueries();
	const { useHistoriesQuery } = useRepIncidentQueries();
	const { clearQuery: clearComplaintQuery } = useComplaintQuery();
	const { clearQuery: clearHistoriesQuery } = useHistoriesQuery(incident?.id);
	const { clearQuery: clearTabCount } = useTabCountQuery(repId);
	const templateData = useMemo(
		() => ({
			incident,
			incidentLists,
		}),
		[incident, incidentLists],
	);
	const [disciplineDialogOpen, setDisciplineDialogOpen] = useState(false);
	const [agentConfirmOpen, setAgentConfirmOpen] = useState(false);
	const [agentConfirmMessage, setAgentConfirmMessage] = useState("");
	const [agentConfirmAction, setAgentConfirmAction] = useState<() => Promise<void>>();
	const [enrollmentConfirmOpen, setEnrollmentConfirmOpen] = useState(false);
	const [enrollmentConfirmMessage, setEnrollmentConfirmMessage] = useState("");
	const [enrollmentConfirmAction, setEnrollmentConfirmAction] = useState<() => Promise<void>>();
	const [enrollmentCancelAction, setEnrollmentCancelAction] = useState<() => void>();
	const [representativeId, setRepresentativeId] = useState<number>(null);
	const [loading, setLoading] = useState(false);

	const save = useCallback(
		async (email: boolean) => {
			const notify = PushProcessingNotification("Saving Complaint");
			try {
				incident.representativeId = incident.representativeId ?? representativeId;
				incident.status = incident.statusId ?? incident.status;
				incident.loaStatus = incident.loaStatusId ?? incident.loaStatus;
				incident.customerContactType = incident.customerContactTypeId ?? incident.customerContactType;
				incident.customerCallAttempt = incident.customerCallAttemptId ?? incident.customerCallAttempt;
				incident.complaintOrIncident = incident.complaintOrIncidentId ?? incident.complaintOrIncident;

				const { documents, documentsChanged, documentsUploaded, ...complaint } = incident;

				if (!incident.id) {
					const doSave = await checkForDuplicates(complaint, notify);
					if (!doSave) return;
				}

				const id = await saveComplaint(complaint, email);
				if (documentsChanged) {
					notify.processing("Saving Documents");
					const allDocs = [...(documents ?? []), ...(documentsUploaded ?? [])];
					await saveComplaintDocs(id, allDocs);
				}
				notify.processing("Loading...");
				const newComplaint = await loadComplaint(id);
				notify.success("Saved!");
				await clearComplaintQuery();
				await clearHistoriesQuery();
				await clearTabCount();
				//Temporary hack to reload page on a new complaint
				if (!complaint.id) {
					reloadTab(id);
				}
				return newComplaint;
			} catch (ex) {
				notify.error("Failed to save");
				throw ex;
			}
		},
		[incident],
	);

	const reloadTab = useCallback((id: number) => {
		if (!(window as any).AgentPortal?.toPortalResult) return;
		(window as any).AgentPortal.toPortalResult("Complaints", id);
	}, []);

	const updateValues = useCallback(updateValuesWrapper(applyBusinessLogic), [applyBusinessLogic]);

	async function updateValueInternal(id: string, value: any, currentState: IState, parentComponent?: string) {
		const { newState } = await updateValues(id, value, currentState, parentComponent);
		return newState ?? currentState;
	}

	async function applyBusinessLogic(id: string, value: any, newState: IState) {
		async function update(name: string, value: any) {
			newState = await updateValueInternal(name, value, newState);
		}

		//Custom logic
		switch (id) {
			case "incident.esidNumber":
				//If No Utility and ESID matches prefix of utility, auto-select that utility
				if (value && !incident.utilityId) {
					const strValue = String(value);
					const matchingUtilities = incidentLists.fullUtilities
						.filter((u) => u.AccountNumberPrefix && strValue.startsWith(u.AccountNumberPrefix))
						.sort((u1, u2) => u2.AccountNumberPrefix.length - u1.AccountNumberPrefix.length)
						.map((u) => u.Id);
					const matchingUtility = matchingUtilities[0];
					if (matchingUtility) {
						await update("incident.utilityId", matchingUtility);
					}
				}
				break;
			case "incident.documents":
			case "incident.documentsUploaded":
				await update("incident.documentsChanged", true);
				break;
			case "incident.complaintTypeName":
				await update("incident.complaintTypeOther", null);
				break;
			case "incident.complaintSourceName":
				await update("incident.complaintSourceOther", null);
				break;
			case "incident.commodityId":
				await update("incident.commodity", value);
				break;
			case "incident.sentDate":
				const complaintType = incidentLists?.complaintTypeLevels.data.find(
					(ct) => ct.id === incident.complaintTypeId,
				);
				const level = parseInt(complaintType?.value ?? "1");

				await update("incident.dueDate", addBusinessDays(value, level));
				break;
			default:
				break;
		}

		return newState;
	}

	const onModalSave = useCallback(async () => {
		await save(false);
	}, [save]);

	const findAgent = useCallback(async () => {
		const { id: complaintId, manualAgentId, manualAgentName } = incident;

		const notify = PushProcessingNotification("Finding Agent...");

		if (!manualAgentId && !manualAgentName)
			return notify.error("You must enter an Agent ID and/or Agent Name in order to find a matching agent.");

		if (manualAgentName?.split(" ").length === 1)
			return notify.error("You must enter the Agent's full name in order to find a matching agent.");

		const response = await findAgentApi(manualAgentId, manualAgentName);

		if (!response) return notify.error("An error occurred finding/assigning agent.");

		const { repId, message } = response;
		if (repId <= 0) return notify.error(message);

		notify.info("Agent Found");
		setAgentConfirmMessage(message);
		setAgentConfirmAction(() => confirm);
		setAgentConfirmOpen(true);

		async function confirm() {
			notify.processing("Setting Agent");
			if (!complaintId) setRepresentativeId(repId);
			else {
				await reassignAgent(complaintId, repId);
				editComplaint({ representativeId: repId });
			}
			notify.success("Agent Set");
			setAgentConfirmOpen(false);
		}
	}, [incident, editComplaint]);

	const findEnrollment = useCallback(async () => {
		setLoading(true);
		const { id: complaintId, customerAccountNumber, esidNumber } = incident;

		const notify = PushProcessingNotification("Finding Enrollment...");

		if (!customerAccountNumber && !esidNumber)
			return onError(
				"You must enter an Customer Account Number and/or ESID Number in order to find a matching enrollment.",
			);

		const response = await findEnrollmentApi(complaintId, customerAccountNumber, esidNumber);

		if (!response) return onError("An error occurred finding/assigning enrollment.");

		const { enrollmentID, message } = response;
		if (enrollmentID <= 0) return onError(message);

		notify.success("Enrollment Found");
		setEnrollmentConfirmMessage(message);
		setEnrollmentConfirmAction(() => confirm);
		setEnrollmentCancelAction(() => cancel);
		setEnrollmentConfirmOpen(true);

		async function confirm() {
			const notify = PushProcessingNotification("Attaching Enrollment...");
			const repId = await attachEnrollmentToComplaint(complaintId, enrollmentID);
			if (repId) {
				const newComplaint = await loadComplaint(complaintId);
				overwriteComplaint({ ...newComplaint, representativeId: repId });
			}
			notify.success("Enrollment Attached");
			setLoading(false);
			setEnrollmentConfirmOpen(false);
		}
		async function cancel() {
			setLoading(false);
			setEnrollmentConfirmOpen(false);
		}
		function onError(error: string) {
			setLoading(false);
			return notify.error(error);
		}
	}, [incident, editComplaint]);

	const attachEnrollment = useCallback(async () => {
		const notify = PushProcessingNotification("Attaching Enrollment...");
		try {
			setLoading(true);
			const { id: complaintId, enrollmentId, attachedEnrollmentId } = incident;
			if (attachedEnrollmentId === enrollmentId) return;
			if (await attachEnrollmentToComplaint(complaintId, `${attachedEnrollmentId}`)) {
				const newComplaint = await loadComplaint(complaintId);
				overwriteComplaint(newComplaint);
			}
			notify.success("Enrollment Attached");
			setLoading(false);
		} catch (ex) {
			notify.error("Failed to attach enrollment");
			setLoading(false);
		}
	}, [incident, editComplaint]);

	const detachEnrollment = useCallback(async () => {
		setLoading(true);
		const notify = PushProcessingNotification("Detaching Enrollment...");
		try {
			const { id: complaintId } = incident;
			if (await attachEnrollmentToComplaint(complaintId, null)) editComplaint({ enrollmentId: null });
			notify.success("Enrollment Detached");
			setLoading(false);
		} catch (ex) {
			notify.error("Failed to detach enrollment");
			setLoading(false);
		}
	}, [incident, editComplaint]);

	const assignQaAnalyst = useCallback(async () => {
		const notify = PushProcessingNotification("Assigning QA Analyst...");
		try {
			const { id: complaintId, assignedAnalystId } = incident;
			if (!assignedAnalystId) return notify.error("You must select a QA Analyst to assign.");

			const analystName = await assignToAnalyst(complaintId, assignedAnalystId);
			if (analystName)
				editComplaint({
					qaAnalystId: assignedAnalystId,
					qaAnalystName: analystName,
				});
			notify.success("QA Analyst Assigned");
			await clearHistoriesQuery();
		} catch (ex) {
			notify.error("Failed to assign QA Analyst");
		}
	}, [incident, editComplaint]);

	const reassignRep = useCallback(async () => {
		const { id: complaintId, assignedRepId } = incident;
		const repId = await assignToRepresentative(complaintId, assignedRepId);
		await clearHistoriesQuery();
		if (repId) editRep({ repId: assignedRepId });
		reloadTab(complaintId);
	}, [incident, editRep]);

	const addDiscipline = useCallback(async () => {
		setDisciplineDialogOpen(true);
	}, []);

	const customFunctions = useCallback(
		(name: string) => {
			switch (name) {
				case "findAgent":
					return findAgent;
				case "findEnrollment":
					return findEnrollment;
				case "attachEnrollment":
					return attachEnrollment;
				case "detachEnrollment":
					return detachEnrollment;
				case "assignQaAnalyst":
					return assignQaAnalyst;
				case "reassignRep":
					return reassignRep;
				case "addDiscipline":
					return addDiscipline;
			}
		},
		[findAgent, findEnrollment, attachEnrollment, detachEnrollment, assignQaAnalyst, reassignRep, addDiscipline],
	);

	return (
		<>
			<EditableItemDynamicTab
				itemContext={context}
				pageName={tabs.complaints}
				otherTemplateData={templateData}
				itemProp="incident"
				updateValues={updateValues}
				save={save}
				customFunctions={customFunctions}
				saveAndEmail={hasValue(incident?.representativeId) && !!incident?.representativeId}
				loaded={listsLoaded && !loading}
			/>
			<BasicDialog
				title="Add Discipline"
				open={disciplineDialogOpen}
				onOpenChange={setDisciplineDialogOpen}
				hideTrigger
				className="md:max-w-[800px]"
				allowOverflow
			>
				<RepDisciplineEditorModal
					rep={rep}
					incidentId={incident?.id}
					onModalSave={onModalSave}
					onClose={() => setDisciplineDialogOpen(false)}
				/>
			</BasicDialog>
			<ConfirmAlertDialog
				isOpen={!!agentConfirmOpen}
				onClose={() => setAgentConfirmOpen(false)}
				onConfirm={agentConfirmAction}
				title="Agent Found"
				description={agentConfirmMessage}
			/>
			<ConfirmAlertDialog
				isOpen={!!enrollmentConfirmOpen}
				onClose={enrollmentCancelAction}
				onConfirm={enrollmentConfirmAction}
				title="Enrollment Found"
				description={enrollmentConfirmMessage}
			/>
		</>
	);
}

export function useComplaintLists() {
	const { repId } = useRepContext();
	const complaintContext = useComplaintContext();
	const entityId = useEntityId();
	const complaintId = complaintContext.item?.id ?? entityId;

	const {
		useComplaintTypesQuery,
		useComplaintTypeLevelsQuery,
		useComplaintSourcesQuery,
		useEnrollmentSourcesQuery,
		useStaffingPartnersQuery,
		useFYIReasonsQuery,
		useReportingTagsQuery,
		useRootCausesQuery,
		useLocationPartnersQuery,
		useBrandsQuery,
		useEnrollmentsQuery,
		useRepsQuery,
		useManagersQuery,
		useUtilitiesQuery,
		useTelesalesBrandsQuery,
	} = useComplaintQueries();

	const { data: complaintTypes, loaded: complaintTypesLoaded } = useComplaintTypesQuery();
	const { data: complaintTypeLevels, loaded: complaintTypeLevelsLoaded } = useComplaintTypeLevelsQuery();
	const { data: complaintSources, loaded: complaintSourcesLoaded } = useComplaintSourcesQuery();
	const { data: enrollmentSources, loaded: enrollmentSourcesLoaded } = useEnrollmentSourcesQuery();
	const { data: staffingPartners, loaded: staffingPartnersLoaded } = useStaffingPartnersQuery();
	const { data: complaintFYIReasons, loaded: complaintFYIReasonsLoaded } = useFYIReasonsQuery();

	const { data: reportingTags, loaded: reportingTagsLoaded } = useReportingTagsQuery();
	const { data: rootCauses, loaded: rootCausesLoaded } = useRootCausesQuery();
	const { data: locationPartners, loaded: locationPartnersLoaded } = useLocationPartnersQuery();

	const { data: brands, loaded: brandsLoaded } = useBrandsQuery();
	const { data: telesalesBrands, loaded: telesalesBrandsLoaded } = useTelesalesBrandsQuery();
	const { data: enrollments, loaded: enrollmentsLoaded } = useEnrollmentsQuery();
	const { data: reps, loaded: repsLoaded } = useRepsQuery();
	const { data: managers, loaded: managersLoaded } = useManagersQuery();

	const { data: utilities, loaded: utilitiesLoaded } = useUtilitiesQuery(!repId ? complaintId : null);

	const [incidentLists, incidentListsLoaded] = useIncidentLists();

	const activeLists = useMemo(() => {
		return {
			...incidentLists,
			complaintOrIncidentTypes: [
				{ id: 0, value: "Complaint" },
				{ id: 1, value: "Incident" },
				{ id: 2, value: "Customer Inquiry" },
				{ id: 3, value: "Quality Review" },
			],
			commodities: [
				{ id: 0, value: "Electric" },
				{ id: 1, value: "Natural Gas" },
				{ id: 2, value: "Dual Fuel" },
			],
			languages: [
				{ id: 2, value: "English" },
				{ id: 3, value: "Spanish" },
			],
			commodityTypes: [
				{ id: 0, value: "Electric" },
				{ id: 1, value: "Natural Gas" },
				{ id: 2, value: "Dual Fuel" },
			],
			manager: managers?.data ?? [],
			complaintTypes: complaintTypes?.data ?? [],
			complaintTypeLevels,
			complaintSources: complaintSources?.data ?? [],
			enrollmentSources: enrollmentSources?.data ?? [],
			locationPartners: locationPartners?.data ?? [],
			staffingPartners: staffingPartners?.data ?? [],
			reportingTags: reportingTags?.data ?? [],
			complaintFYIReasons: complaintFYIReasons?.data ?? [],
			brands: brands?.data ?? [],
			telesalesBrands: telesalesBrands?.data ?? [],
			enrollments: enrollments?.data ?? [],
			reps: reps?.data ?? [],
			utilities: getItems(utilities, utilitiesLoaded),
			fullUtilities: utilities?.data ?? [],
			rootCause: rootCauses?.data ?? [],
		};
	}, [
		incidentLists,
		managers?.data,
		complaintTypes?.data,
		complaintTypeLevels,
		complaintSources?.data,
		enrollmentSources?.data,
		locationPartners?.data,
		staffingPartners?.data,
		reportingTags?.data,
		rootCauses?.data,
		complaintFYIReasons?.data,
		brands?.data,
		telesalesBrands?.data,
		enrollments?.data,
		reps?.data,
		utilities,
		utilitiesLoaded,
	]);

	const allLoaded =
		complaintTypesLoaded &&
		complaintTypeLevelsLoaded &&
		complaintSourcesLoaded &&
		enrollmentSourcesLoaded &&
		staffingPartnersLoaded &&
		complaintFYIReasonsLoaded &&
		reportingTagsLoaded &&
		rootCausesLoaded &&
		locationPartnersLoaded &&
		brandsLoaded &&
		enrollmentsLoaded &&
		repsLoaded &&
		utilitiesLoaded &&
		telesalesBrandsLoaded &&
		incidentListsLoaded &&
		managersLoaded;

	return {
		activeLists,
		allLoaded,
	};
}

export function useDuplicateCheck() {
	const { checkDuplicates } = useComplaintApi();
	const { updateAlert, clearAlert } = useAlertContext();
	/**
	 * Returns true if save continues
	 */
	const checkForDuplicates = useCallback(
		async (complaint: Complaint, notify: ProcessingNotification) => {
			notify.processing("Checking for Duplicates...");
			const duplicates = await checkDuplicates(complaint);
			if (duplicates.length) {
				const promise = new Promise<boolean>((resolve) => {
					notify.processing("Duplicates found...");
					updateAlert({
						titleText: "Potential Duplicates Found",
						body: <PotentialDuplicatesGrid complaint={complaint} duplicates={duplicates} />,
						onConfirm: () => {
							clearAlert();
							resolve(true);
						},
						confirmText: "Continue",
						onCancel: () => {
							clearAlert();
							resolve(false);
						},
						cancelText: "Cancel",
						type: "warning",
						size: "auto",
					});
				});
				const result = await promise;
				if (result) notify.processing("Duplicates Bypassed...");
				else notify.info("Stopped");
				return result;
			}
			notify.processing("No Duplicates found, Continuing...");
			return true;
		},
		[checkDuplicates, clearAlert, updateAlert],
	);
	return { checkForDuplicates };
}
